import { createContext, ReactNode, useReducer, useContext } from 'react';
import { UserProfile } from '../types/common.types';
import { ActionMap, AuthState } from '../types/auth.types';
import { UserAccountState } from '../enums/shared.enums';

// ----------------------------------------------------------------------

export enum Types {
    Login = 'LOGIN',
    Logout = 'LOGOUT',
    Initialize = 'INITIALIZE',
}

type AuthStatePayload = {
    [Types.Login]: {
        profile: UserProfile;
    };
    [Types.Logout]: undefined;
    [Types.Initialize]: undefined;
};

export type AuthStateActions = ActionMap<AuthStatePayload>[keyof ActionMap<AuthStatePayload>];

const initialState: AuthState = {
    isAuthenticated: false,
    isInitialized: false,
    profile: null,
};

const authStateReducer = (state: AuthState, action: AuthStateActions) => {
    switch (action.type) {
        case 'LOGIN':
            return {
                ...state,
                isAuthenticated: true,
                isInitialized: true,
                profile: action.payload.profile,
            };
        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                isInitialized: true,
                profile: null,
            };
        case 'INITIALIZE':
            return {
                ...state,
                isInitialized: true,
            };
        default:
            return state;
    }
};

// ----------------------------------------------------------------------

export interface AuthStateContextType {
    // State
    isAuthenticated: boolean;
    isInitialized: boolean;
    isOnboarded: boolean;
    profile: UserProfile | null;
    
    // Actions
    dispatch: React.Dispatch<AuthStateActions>;
}

const AuthStateContext = createContext<AuthStateContextType | undefined>(undefined);

// ----------------------------------------------------------------------

interface AuthStateProviderProps {
    children: ReactNode;
}

export const AuthStateProvider: React.FC<AuthStateProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(authStateReducer, initialState);

    const contextValue: AuthStateContextType = {
        // State
        isAuthenticated: state.isAuthenticated,
        isInitialized: state.isInitialized,
        isOnboarded: state.profile?.state !== UserAccountState.REGISTERED,
        profile: state.profile,
        
        // Actions
        dispatch,
    };

    return (
        <AuthStateContext.Provider value={contextValue}>
            {children}
        </AuthStateContext.Provider>
    );
};

/**
 * Hook to access auth state - throws error if used outside provider
 */
const useAuthState = (): AuthStateContextType => {
    const context = useContext(AuthStateContext);
    
    if (context === undefined) {
        throw new Error('useAuthState must be used within an AuthStateProvider');
    }
    
    return context;
};

export default useAuthState;